// Pure offline photo-sync orchestration. Like sync.ts but for the binary photo
// outbox: no IndexedDB, no fetch -- the store and the upload are injected, so the
// risky logic (draining the photo queue without losing or duplicating an engineer's
// captures) is fully unit-testable in node. The photo binary lives in IndexedDB as a
// Blob so it survives a reload / the app being killed in a no-signal basement.
import type { QueuedPhoto, PhotoSendResult } from "./types";

export interface PhotoOutboxStore {
  getPhotoOutbox(): Promise<QueuedPhoto[]>;
  putPhoto(item: QueuedPhoto): Promise<void>;
  deletePhoto(id: string): Promise<void>;
}

export type PhotoSendFn = (item: QueuedPhoto) => Promise<PhotoSendResult>;

export interface PhotoFlushSummary {
  synced: number;
  dropped: number;
  failed: number;
  remaining: number;
}

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return typeof err === "string" ? err : "Upload failed";
}

// Map an HTTP status to a send outcome. 404/410 (the job is gone) drop the photo
// rather than retry forever. 401/403/4xx are kept (a re-login or fix may let them
// through) and surface as an error on the photo -- nothing is silently discarded.
//
// 409 IS ALSO DROPPED (Session APF, PWA audit finding 14 / fix 15). It is the ONLY 409
// POST /api/jobs/:id/photos returns (server/src/modules/jobs/job.routes.ts, error "photo_limit"):
// the job sheet already holds MAX_JOB_PHOTOS and this upload would take it past the cap. No amount
// of retrying changes that answer -- the sheet does not shrink on its own -- so keeping the item
// would leave it stuck in the outbox for ever, retried on every reconnect with no way for the
// engineer to clear it from the phone. The phone's own picker already caps an engineer at the same
// six (MAX_JOB_PHOTOS, src/routes/engineer/JobDetail.tsx), so this is reachable only by a race with
// another device or an office-side upload, never by ordinary single-device use. If a second 409 is
// ever added to that route with a different meaning, this blanket rule would wrongly drop it too --
// check job.routes.ts before adding one, or narrow this to the response body's `error` field instead
// of the bare status.
export function classifyPhotoSendError(status: number): PhotoSendResult {
  if (status === 404 || status === 410 || status === 409) return { ok: false, drop: true };
  return { ok: false, drop: false, error: `Server responded ${status}` };
}

// Session PJ2: a SURVEY photo's refusals, which differ from a job photo's in two ways, both so nothing is lost in silence.
//   404 / 410  the survey (or the visit) was removed by the office. Dropped, AND the engineer is told which photo went.
//   403        the visit is no longer this engineer's. Kept, and ESCALATED: it stops retrying and the survey sheet shows it,
//              rather than retrying for ever on every reconnect with nobody told.
//   409        the survey is closed (reviewed or replaced) or full. Escalated for the same reason: the engineer decides. The reply's
//              error code says which (Session PJ2F2): "photo_limit" is a full survey, anything else (or an unreadable body) closed.
//   400        the server will never take this upload as it is: a slot no longer on the survey, a bad photo id, no file, or a
//              file that is not a photo it can store. Escalated: retrying sends the same bytes and gets the same answer.
//   413        the photo is too large (15 MB at most). Escalated for the same reason.
//   anything else is kept and retried, exactly as a job photo is.
// A job photo's classification (classifyPhotoSendError above) is deliberately unchanged.
export function classifySurveyPhotoSendError(status: number, label: string, errorCode?: unknown): PhotoSendResult {
  if (status === 404 || status === 410) {
    return { ok: false, drop: true, notice: `A photo for ${label} could not be sent: that survey was removed by the office.` };
  }
  if (status === 400) {
    return { ok: false, drop: false, escalate: true, error: "This photo could not be added to the survey: it is not a photo the survey can store, or its place on the survey has gone. Remove it and take it again, or ring the office." };
  }
  if (status === 413) {
    return { ok: false, drop: false, escalate: true, error: "This photo is too large to send (15 MB at most). Remove it and take it again." };
  }
  if (status === 403) {
    return { ok: false, drop: false, escalate: true, error: "This visit is no longer assigned to you. The office needs to assign it back before this photo can be sent." };
  }
  if (status === 409 && errorCode === "photo_limit") {
    return { ok: false, drop: false, escalate: true, error: "This survey already has the most photos it can hold. Ask the office to remove one, or leave this one out." };
  }
  if (status === 409) {
    return { ok: false, drop: false, escalate: true, error: "The office has closed this survey, so this photo cannot be added. Ring the office if it is needed." };
  }
  return { ok: false, drop: false, error: `Server responded ${status}` };
}

export interface PhotoFlushHooks {
  // A dropped photo the engineer must be told about (a survey photo whose survey was removed).
  onNotice?: (notice: string) => void | Promise<void>;
}

// Drain the photo outbox once. Processes items in turn, never throwing: a failed
// item is kept for the next attempt, a permanently-gone one is dropped, a success is
// removed. remaining == failed (every item ends in exactly one bucket).
// Session PJ2: an ESCALATED item (a survey photo only) is skipped, like an escalated completion: another identical attempt
// cannot help. It stays in the outbox, counted in `remaining`, until the engineer removes it or asks for another try.
export async function flushPhotoOutbox(store: PhotoOutboxStore, send: PhotoSendFn, hooks: PhotoFlushHooks = {}): Promise<PhotoFlushSummary> {
  const items = await store.getPhotoOutbox();
  let synced = 0;
  let dropped = 0;
  let failed = 0;
  let escalated = 0;

  for (const item of items) {
    if (item.status === "escalated") {
      escalated += 1;
      continue;
    }
    let result: PhotoSendResult;
    try {
      result = await send(item);
    } catch (err) {
      result = { ok: false, drop: false, error: errorMessage(err) };
    }

    if (result.ok) {
      await store.deletePhoto(item.id);
      synced += 1;
    } else if (result.drop) {
      await store.deletePhoto(item.id);
      dropped += 1;
      if (result.notice) {
        try {
          await hooks.onNotice?.(result.notice);
        } catch {
          /* best effort: telling the engineer must never stop the drain */
        }
      }
    } else {
      await store.putPhoto({
        ...item,
        attempts: item.attempts + 1,
        lastError: result.error,
        ...(result.escalate ? { status: "escalated" as const } : {}),
      });
      failed += 1;
    }
  }

  return { synced, dropped, failed, remaining: failed + escalated };
}
